import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import type { Conversation, Message, Tag, Contact } from '../types';
import {
  Send,
  UserCheck,
  XCircle,
  Phone,
  Building2,
  ArrowRightCircle,
  // Tag as TagIcon, // TODO: Re-enable when tags button is uncommented
  X,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  History,
  UserPlus,
  // TODO: Descomentar quando ASC Brazil implementar webhook de read receipts
  // Check,
  // CheckCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { TransferConversationModal } from '../components/TransferConversationModal';
import { ConversationHistoryModal } from '../components/ConversationHistoryModal';

export const ConversationDetail: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const id = conversationId;
  const { agent, isAdmin, isDepartmentManager } = useAuth();
  const { systemName } = useSettings();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAssignTagModal, setShowAssignTagModal] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [currentContact, setCurrentContact] = useState<Contact | null>(null);
  const [showTransferSuccessModal, setShowTransferSuccessModal] = useState(false);
  const [tagModalError, setTagModalError] = useState<string | null>(null);
  const [tagModalSuccess, setTagModalSuccess] = useState<string | null>(null);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [showAddContactResultModal, setShowAddContactResultModal] = useState(false);
  const [addContactResult, setAddContactResult] = useState<{ success: boolean; message: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Format date/time in São Paulo timezone
  const formatSaoPauloDateTime = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const saoPauloTime = toZonedTime(dateObj, 'America/Sao_Paulo');
    return format(saoPauloTime, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  useEffect(() => {
    if (id) {
      // Load all data in parallel for faster loading
      loadAllDataInParallel();

      // Connect to WebSocket
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      console.log('🔌 [ConversationDetail] Initializing WebSocket connection to:', apiUrl);

      const socket = io(apiUrl, {
        // Use polling first (more reliable with proxies), upgrade to websocket if available
        transports: ['polling', 'websocket'],
        upgrade: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
      });

      socketRef.current = socket;
      console.log('🔌 [ConversationDetail] Socket created, waiting for connection...');

      socket.on('connect', () => {
        console.log('✅ [ConversationDetail] WebSocket connected, socket ID:', socket.id);
        console.log('🔗 [ConversationDetail] Joining conversation room:', id);
        socket.emit('join-conversation', id);
      });

      socket.on('new-message', (message: Message) => {
        console.log('📩 [ConversationDetail] New message received via WebSocket:', {
          id: message.id,
          content: message.content,
          senderType: message.senderType,
        });
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === message.id)) {
            console.log('⚠️ Duplicate message ignored:', message.id);
            return prev;
          }
          console.log('✅ Message added to state');
          return [...prev, message];
        });
      });

      // TODO: Descomentar quando ASC Brazil implementar webhook de read receipts
      // Esses listeners atualizam os checkmarks em tempo real quando o cliente lê a mensagem
      /*
      socket.on('messages-read', (data: { conversationId: string; count: number }) => {
        console.log('✓✓ [ConversationDetail] Messages marked as read via WebSocket:', data);
        // Only update if it's for this conversation
        if (data.conversationId === id) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.senderType === 'AGENT' && !msg.isRead
                ? { ...msg, isRead: true }
                : msg
            )
          );
          console.log('✅ Agent messages updated to read status');
        }
      });

      socket.on('message-read', (data: { messageId: string; conversationId: string; timestamp: string }) => {
        console.log('✓✓ [ConversationDetail] Single message marked as read via WebSocket:', data);
        // Only update if it's for this conversation
        if (data.conversationId === id) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === data.messageId
                ? { ...msg, isRead: true }
                : msg
            )
          );
          console.log(`✅ Message ${data.messageId} updated to read status`);
        }
      });
      */

      socket.on('conversation-updated', async (data: { conversationId: string; action?: string }) => {
        console.log('🔄 [ConversationDetail] Conversation updated via WebSocket:', data);
        console.log('🔍 [ConversationDetail] Current conversation ID:', id, '| Event conversation ID:', data.conversationId);
        // If this conversation was updated (closed/transferred/assigned), reload it silently
        if (data.conversationId === id) {
          console.log('✅ [ConversationDetail] Refreshing current conversation due to update (action:', data.action, ')');
          try {
            // Skip cache to get fresh data
            const conversationData = await api.getConversation(id, true);
            setConversation(conversationData);
            console.log('✅ [ConversationDetail] Conversation refreshed silently');
            console.log('✅ [ConversationDetail] New status:', conversationData.status);
            console.log('✅ [ConversationDetail] New department ID:', conversationData.department?.id);
            console.log('✅ [ConversationDetail] New department name:', conversationData.department?.name);
            console.log('✅ [ConversationDetail] Assigned agent ID:', conversationData.assignedAgentId);
            console.log('✅ [ConversationDetail] Assigned agent name:', conversationData.assignedAgent?.name);
            console.log('✅ [ConversationDetail] My agent ID:', agent?.id);
            console.log('✅ [ConversationDetail] My department ID:', agent?.department?.id);
            console.log('🔍 [ConversationDetail] Should show transfer button?',
              conversationData.department?.id === agent?.department?.id ||
              conversationData.department === null ||
              conversationData.assignedAgentId === agent?.id
            );
          } catch (error: any) {
            console.error('❌ [ConversationDetail] Failed to refresh conversation:', error);

            // If 403 Forbidden, it means we no longer have access (e.g., conversation was transferred to another department)
            if (error.response?.status === 403) {
              console.log('🚫 [ConversationDetail] Access denied - conversation likely transferred. Showing success modal...');
              // Show success modal (user will click to go back)
              setTimeout(() => {
                setShowTransferSuccessModal(true);
              }, 300);
            }
          }
        } else {
          console.log('ℹ️ [ConversationDetail] Event for different conversation, ignoring');
        }
      });

      socket.on('disconnect', () => {
        console.log('❌ WebSocket disconnected');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ WebSocket connection error:', error);
      });

      // Cleanup on unmount
      return () => {
        socket.emit('leave-conversation', id);
        socket.disconnect();
      };
    }
  }, [id]);

  // Autofocus on message input when conversation loads
  useEffect(() => {
    if (conversation && !isLoading && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [conversation, isLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAllDataInParallel = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Execute all API calls in parallel
      const [conversationData, tagsData] = await Promise.all([
        api.getConversation(id!),
        api.getTags(),
      ]);

      // Set conversation and messages
      setConversation(conversationData);
      if (conversationData.messages) {
        setMessages(conversationData.messages);
      }

      // Set tags
      setTags(tagsData);

      // Set contact if it comes in the response
      if (conversationData.contact) {
        setCurrentContact(conversationData.contact as Contact);
        // Handle both tag formats
        const tagIds = conversationData.contact.tags?.map(tag =>
          'tag' in tag ? tag.tag.id : tag.id
        ) || [];
        setSelectedTagIds(tagIds);
      } else {
        setCurrentContact(null);
        setSelectedTagIds([]);
      }
    } catch (error: any) {
      console.error('Failed to load data:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load conversation';

      if (errorMessage.includes('different department') && !isLoading) {
        console.log('Access denied - conversation was likely transferred');
        return;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadContact = async (phone: string): Promise<Contact | null> => {
    try {
      // Normalize phone - get last 9 digits only
      const phoneDigits = phone.replace(/\D/g, '');
      const last9Digits = phoneDigits.slice(-9);

      // Single search with last 9 digits
      const response = await api.getContacts({ search: last9Digits });

      if (response.data.contacts.length > 0) {
        const foundContact = response.data.contacts[0]; // Backend returns exact match
        console.log('Found contact:', foundContact.id);
        setCurrentContact(foundContact);
        setSelectedTagIds(foundContact.tags?.map((ct: any) => ct.tag.id) || []);
        return foundContact;
      } else {
        console.log('No contact found for phone:', phone);
        setCurrentContact(null);
        setSelectedTagIds([]);
        return null;
      }
    } catch (error) {
      console.error('Failed to load contact:', error);
      setCurrentContact(null);
      setSelectedTagIds([]);
      return null;
    }
  };

  const loadConversation = async (skipContactLoad = false) => {
    try {
      setError(null);
      const data = await api.getConversation(id!);
      console.log('Loaded conversation:', data);
      setConversation(data);
      if (data.messages) {
        setMessages(data.messages);
      }
      // Load contact data to get tags - only on first load, not on polling
      if (!skipContactLoad) {
        await loadContact(data.customerPhone);
      }
    } catch (error: any) {
      console.error('Failed to load conversation:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load conversation';

      // If access denied due to different department, don't set error - might be due to recent transfer
      if (errorMessage.includes('different department') && !isLoading) {
        // Silently redirect to conversations list - transfer was likely successful
        console.log('Access denied - conversation was likely transferred');
        return;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const MAX_SIZE_MB = 10;
      const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

      // Check file size (10MB limit)
      if (file.size > MAX_SIZE_BYTES) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        alert(
          `❌ Arquivo muito grande!\n\n` +
          `Tamanho do arquivo: ${fileSizeMB} MB\n` +
          `Tamanho máximo permitido: ${MAX_SIZE_MB} MB\n\n` +
          `Por favor, selecione um arquivo menor.`
        );
        // Clear the input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || isSending) return;

    setIsSending(true);
    setIsUploading(true);
    try {
      let messageContent = newMessage.trim();
      let messageType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' = 'TEXT';
      let mediaUrl: string | undefined;

      // Upload file if present
      if (selectedFile) {
        const uploadResponse = await api.uploadFile(selectedFile);
        mediaUrl = uploadResponse.fileUrl;

        // Determine message type based on file MIME type
        if (selectedFile.type.startsWith('image/')) {
          messageType = 'IMAGE';
          messageContent = messageContent || '[Imagem]';
        } else if (selectedFile.type.startsWith('video/')) {
          messageType = 'VIDEO';
          messageContent = messageContent || '[Vídeo]';
        } else if (selectedFile.type.startsWith('audio/')) {
          messageType = 'AUDIO';
          messageContent = messageContent || '[Áudio]';
        } else {
          messageType = 'DOCUMENT';
          messageContent = messageContent || selectedFile.name;
        }
      }

      await api.sendMessage(id!, {
        content: messageContent,
        messageType,
        mediaUrl
      });

      setNewMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadConversation(true); // Skip contact reload
    } catch (error: any) {
      console.error('Failed to send message:', error);
      alert(error.response?.data?.error || 'Falha ao enviar mensagem');
    } finally {
      setIsUploading(false);
      setIsSending(false);
    }
  };

  const handleAssignToMe = async () => {
    try {
      setIsAssigning(true);
      setAssignError(null);
      await api.assignConversation(id!, { agentId: agent!.id });
      await loadConversation(true); // Skip contact reload
      setShowAssignModal(true);
    } catch (error: any) {
      console.error('Failed to assign conversation:', error);
      setAssignError(error.response?.data?.error || 'Falha ao atribuir atendimento');
      setShowAssignModal(true);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCloseConversation = async () => {
    setIsClosing(true);
    try {
      await api.closeConversation(id!);
      setShowCloseModal(false);
      await loadConversation(true); // Skip contact reload
    } catch (error: any) {
      console.error('Failed to close conversation:', error);
      alert('Falha ao encerrar atendimento: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsClosing(false);
      setShowCloseModal(false);
    }
  };

  const handleAddContact = async () => {
    if (!conversation) return;

    setIsAddingContact(true);
    try {
      await api.createContact({
        name: conversation.customerName || 'Cliente',
        phone: conversation.customerPhone,
      });

      setAddContactResult({
        success: true,
        message: 'Contato adicionado com sucesso!',
      });
      setShowAddContactModal(false);
      setShowAddContactResultModal(true);

      // Reload conversation to update contact info
      await loadConversation(false);
    } catch (error: any) {
      console.error('Failed to add contact:', error);
      const errorMessage = error.response?.data?.error || 'Falha ao adicionar contato';

      setAddContactResult({
        success: false,
        message: errorMessage,
      });
      setShowAddContactModal(false);
      setShowAddContactResultModal(true);
    } finally {
      setIsAddingContact(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-gray-800 animate-pulse">
        {/* Header Skeleton */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <div className="space-y-3 flex-1">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              </div>
            </div>
            <div className="flex space-x-2">
              <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>

        {/* Messages Skeleton */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Customer message skeleton */}
          <div className="flex justify-start">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-3 max-w-[70%] space-y-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-48"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
            </div>
          </div>

          {/* Agent message skeleton */}
          <div className="flex justify-end">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-3 max-w-[70%] space-y-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-40"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-56"></div>
            </div>
          </div>

          {/* Customer message skeleton */}
          <div className="flex justify-start">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-3 max-w-[70%] space-y-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-64"></div>
            </div>
          </div>

          {/* Loading indicator */}
          <div className="flex justify-center">
            <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Carregando conversa...</span>
            </div>
          </div>
        </div>

        {/* Input Skeleton */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex space-x-2">
            <div className="h-10 flex-1 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400 mb-4">Erro: {error}</p>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Atendimento não encontrado</p>
        </div>
      </div>
    );
  }

  const canSendMessage =
    conversation.status !== 'CLOSED' &&
    (conversation.assignedAgentId === agent?.id ||
     isAdmin() ||
     isDepartmentManager());

  return (
    <>
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {conversation.customerName || conversation.customerPhone}
                </h2>
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-mono font-semibold">
                  #{conversation.protocolId}
                </span>
                {/* Contact tags */}
                {conversation.contact?.tags && conversation.contact.tags.length > 0 && (
                  conversation.contact.tags.map((tag) => {
                    // Handle both ConversationContact (simplified) and Contact (full) types
                    const tagColor = 'color' in tag ? tag.color : tag.tag?.color;
                    const tagName = 'name' in tag ? tag.name : tag.tag?.name;
                    return (
                      <span
                        key={tag.id}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: tagColor }}
                      >
                        {tagName}
                      </span>
                    );
                  })
                )}
              </div>
              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center space-x-1">
                  <Phone className="w-4 h-4" />
                  <span>{conversation.customerPhone}</span>
                </span>
                {conversation.department && (
                  <span className="flex items-center space-x-1">
                    <Building2 className="w-4 h-4" />
                    <span>{conversation.department.name}</span>
                  </span>
                )}
                {conversation.assignedAgent && (
                  <span className="flex items-center space-x-1">
                    <UserCheck className="w-4 h-4" />
                    <span>{conversation.assignedAgent.name}</span>
                  </span>
                )}
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    conversation.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-700'
                      : conversation.status === 'CLOSED'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {conversation.status}
                </span>
              </div>
              {/* Contact Tags */}
              {currentContact && currentContact.tags && currentContact.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {currentContact.tags.map((ct) => (
                    <span
                      key={ct.id}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: ct.tag.color }}
                    >
                      {ct.tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-1.5">
            {/* TODO: Re-enable after tags functionality is fully tested
            <button
              onClick={() => {
                setTagModalError(null);
                setTagModalSuccess(null);
                setShowAssignTagModal(true);
              }}
              className="h-9 px-2.5 lg:px-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center space-x-1.5"
              title="Atribuir Etiquetas ao Contato"
            >
              <TagIcon className="w-4 h-4 flex-shrink-0" />
              <span className="hidden lg:inline">Etiquetas</span>
            </button>
            */}
            <button
              onClick={() => setShowHistoryModal(true)}
              className="h-9 px-2.5 lg:px-3 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded-lg transition flex items-center space-x-1.5 whitespace-nowrap"
              title="Ver histórico de conversas"
            >
              <History className="w-4 h-4 flex-shrink-0" />
              <span className="hidden lg:inline">Histórico</span>
            </button>
            <button
              onClick={() => setShowAddContactModal(true)}
              className="h-9 px-2.5 lg:px-3 bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 text-white rounded-lg transition flex items-center space-x-1.5 whitespace-nowrap"
              title="Adicionar à lista de contatos"
            >
              <UserPlus className="w-4 h-4 flex-shrink-0" />
              <span className="hidden lg:inline">Adicionar</span>
            </button>
            {!conversation.assignedAgentId && conversation.status !== 'CLOSED' && (
              <button
                onClick={handleAssignToMe}
                disabled={isAssigning}
                className="h-9 px-2.5 lg:px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title="Atribuir conversa para mim"
              >
                {isAssigning ? (
                  <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                ) : (
                  <UserCheck className="w-4 h-4 flex-shrink-0" />
                )}
                <span className="hidden lg:inline">{isAssigning ? 'Atribuindo...' : 'Atribuir'}</span>
              </button>
            )}
            {conversation.status !== 'CLOSED' && (() => {
              // Admins can always transfer
              if (isAdmin()) return true;

              // Department managers can transfer in their department
              if (isDepartmentManager() && (conversation.department?.id === agent?.department?.id || conversation.department === null)) {
                return true;
              }

              // Regular agents can only transfer their own conversations in their department
              if (conversation.assignedAgentId === agent?.id &&
                  (conversation.department?.id === agent?.department?.id || conversation.department === null)) {
                return true;
              }

              return false;
            })() && (
              <button
                onClick={() => setShowTransferModal(true)}
                className="h-9 px-2.5 lg:px-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center space-x-1.5 whitespace-nowrap"
                title="Transferir conversa"
              >
                <ArrowRightCircle className="w-4 h-4 flex-shrink-0" />
                <span className="hidden lg:inline">Transferir</span>
              </button>
            )}
            {conversation.status !== 'CLOSED' && (() => {
              // Admins and General Managers can always close
              if (isAdmin() || agent?.role === 'GENERAL_MANAGER') return true;

              // Regular agents can only close their own conversations in their department
              if (conversation.assignedAgentId === agent?.id &&
                  (conversation.department?.id === agent?.department?.id || conversation.department === null)) {
                return true;
              }

              return false;
            })() && (
              <button
                onClick={() => setShowCloseModal(true)}
                className="h-9 px-2.5 lg:px-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center space-x-1.5 whitespace-nowrap"
                title="Encerrar atendimento"
              >
                <XCircle className="w-4 h-4 flex-shrink-0" />
                <span className="hidden lg:inline">Encerrar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">Nenhuma mensagem ainda</div>
        ) : (
          messages.map((message) => {
            const isCustomer = message.senderType === 'CUSTOMER';
            const isSystem = message.senderType === 'SYSTEM';
            const isTransferNotification = isSystem && message.content.toLowerCase().includes('transferida');

            // Render transfer notifications as centered badges (WhatsApp-style)
            if (isTransferNotification) {
              return (
                <div key={message.id} className="flex justify-center my-2">
                  <div className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-full shadow-sm">
                    <p className="text-xs text-center whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              );
            }

            // Render other system messages as before (green, right-aligned)
            if (isSystem) {
              return (
                <div key={message.id} className="flex justify-end">
                  <div className="bg-green-600 dark:bg-green-700 text-white px-4 py-2 rounded-lg max-w-[80%]">
                    <p className="text-xs whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs mt-1 text-green-100">
                      {message.timestamp
                        ? formatSaoPauloDateTime(message.timestamp)
                        : formatSaoPauloDateTime(new Date())}
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={message.id}
                className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[70%] ${
                    isCustomer
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                      : 'bg-blue-600 text-white'
                  } rounded-lg px-4 py-2`}
                >
                  {isCustomer && (
                    <p className="text-xs opacity-75 mb-1 font-semibold">
                      {conversation.customerName || conversation.customerPhone}
                    </p>
                  )}
                  {!isCustomer && message.agent && (
                    <p className="text-xs opacity-75 mb-1 font-semibold">
                      {message.agent.name} - {message.agent.department?.name || 'Departamento'} - {systemName}
                    </p>
                  )}

                  {/* Render media based on messageType */}
                  {message.messageType === 'IMAGE' && message.mediaUrl && (
                    <div className="mb-2">
                      <img
                        src={message.mediaUrl}
                        alt="Imagem"
                        className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition"
                        onClick={() => window.open(message.mediaUrl, '_blank')}
                      />
                    </div>
                  )}

                  {message.messageType === 'VIDEO' && message.mediaUrl && (
                    <div className="mb-2">
                      <video
                        controls
                        className="max-w-full rounded-lg"
                        src={message.mediaUrl}
                      >
                        Seu navegador não suporta vídeos.
                      </video>
                    </div>
                  )}

                  {message.messageType === 'AUDIO' && message.mediaUrl && (
                    <div className="mb-2">
                      <audio
                        controls
                        className="w-full"
                        src={message.mediaUrl}
                      >
                        Seu navegador não suporta áudio.
                      </audio>
                    </div>
                  )}

                  {message.messageType === 'DOCUMENT' && message.mediaUrl && (
                    <div className="mb-2 flex items-center space-x-2 p-2 bg-white dark:bg-gray-600 rounded">
                      <FileText className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="text-sm flex-1 truncate text-gray-700 dark:text-gray-200">
                        {message.originalFilename || message.mediaUrl.split('/').pop() || 'Documento'}
                      </span>
                      <a
                        href={message.mediaUrl}
                        download={message.originalFilename}
                        className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  )}

                  {/* Text content */}
                  {(message.messageType === 'TEXT' || !message.mediaUrl) && (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  )}

                  {/* Caption for media messages (including DOCUMENT) */}
                  {message.mediaUrl && message.messageType !== 'TEXT' && message.content !== '[Imagem]' && message.content !== '[Vídeo]' && message.content !== '[Áudio]' && message.content !== '[Documento]' && (
                    <p className="text-sm whitespace-pre-wrap mt-2">{message.content}</p>
                  )}

                  <div
                    className={`flex items-center justify-end space-x-1 text-xs mt-1 ${
                      isCustomer ? 'text-gray-500 dark:text-gray-400' : 'text-blue-100'
                    }`}
                  >
                    <span>
                      {message.timestamp
                        ? formatSaoPauloDateTime(message.timestamp)
                        : formatSaoPauloDateTime(new Date())}
                    </span>
                    {/* TODO: Descomentar quando ASC Brazil implementar webhook de read receipts
                        Checkmarks para mensagens do agente:
                        - Check simples (✓) = mensagem enviada
                        - CheckCheck duplo (✓✓) = mensagem lida pelo cliente
                    */}
                    {/* {!isCustomer && (
                      <span className="ml-1">
                        {message.isRead ? (
                          <CheckCheck className="w-4 h-4 inline" />
                        ) : (
                          <Check className="w-4 h-4 inline" />
                        )}
                      </span>
                    )} */}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      {canSendMessage ? (
        <form onSubmit={handleSendMessage} className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
          {/* File preview */}
          {selectedFile && (
            <div className="mb-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {selectedFile.type.startsWith('image/') && <ImageIcon className="w-5 h-5 text-blue-600" />}
                {selectedFile.type.startsWith('video/') && <Video className="w-5 h-5 text-purple-600" />}
                {selectedFile.type.startsWith('audio/') && <Music className="w-5 h-5 text-green-600" />}
                {!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/') && !selectedFile.type.startsWith('audio/') && (
                  <FileText className="w-5 h-5 text-gray-600" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                disabled={isSending}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          <div className="flex items-center space-x-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              disabled={isSending}
            />

            {/* Attach file button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Anexar arquivo"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            <textarea
              ref={messageInputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                // Send message on Enter (without Shift)
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e as any);
                }
              }}
              placeholder="Digite uma mensagem... (Enter para enviar, Shift+Enter para nova linha)"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none min-h-[40px] max-h-[120px] overflow-y-auto"
              disabled={isSending}
              rows={1}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              translate="no"
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
            />
            <button
              type="submit"
              disabled={isSending || (!newMessage.trim() && !selectedFile)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center space-x-2"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Enviar</span>
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800">
          {conversation.status === 'CLOSED' ? (
            <p>Este atendimento está fechado</p>
          ) : (
            <p>Você não pode enviar mensagens para este atendimento</p>
          )}
        </div>
      )}
    </div>

    {/* Transfer Conversation Modal */}
    <TransferConversationModal
      isOpen={showTransferModal}
      onClose={() => setShowTransferModal(false)}
      onSuccess={(isDifferentDepartment) => {
        if (isDifferentDepartment) {
          // Show success modal and redirect
          setShowTransferSuccessModal(true);
        } else {
          // Same department transfer, just reload
          loadConversation(true); // Skip contact reload
        }
      }}
      conversationId={id!}
      currentDepartmentId={conversation?.departmentId}
      currentAgentId={conversation?.assignedAgentId}
    />

    {/* Transfer Success Modal */}
    {showTransferSuccessModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 text-center">
            Transferência Concluída!
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 text-center text-sm">
            A conversa foi transferida com sucesso para outro departamento.
            <br />
            <br />
            Você não tem mais acesso a esta conversa.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                setShowTransferSuccessModal(false);
                window.location.href = '/conversations';
              }}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition font-medium shadow-md hover:shadow-lg"
            >
              Voltar para Conversas
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Assign Modal */}
    {showAssignModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-4">
            <div className={`w-12 h-12 ${assignError ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'} rounded-full flex items-center justify-center`}>
              {assignError ? (
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              ) : (
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              )}
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
            {assignError ? 'Erro ao Atribuir' : 'Atendimento Atribuído'}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
            {assignError || 'O atendimento foi atribuído a você com sucesso.'}
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                setShowAssignModal(false);
                setAssignError(null);
              }}
              className={`px-6 py-2 ${assignError ? 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'} text-white rounded-lg transition`}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Close Confirmation Modal */}
    {showCloseModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Encerrar Atendimento
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Tem certeza que deseja encerrar este atendimento? Esta ação não pode ser desfeita.
          </p>
          {isClosing && (
            <div className="mb-4 flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-sm">Encerrando atendimento...</span>
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowCloseModal(false)}
              disabled={isClosing}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleCloseConversation}
              disabled={isClosing}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isClosing ? 'Encerrando...' : 'Encerrar'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Assign Tags Modal */}
    {showAssignTagModal && conversation && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold dark:text-white">
              Atribuir Etiquetas ao Contato
            </h2>
            <button
              onClick={() => {
                setShowAssignTagModal(false);
                setTagModalError(null);
                setTagModalSuccess(null);
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Cliente: <strong className="text-gray-900 dark:text-white">{conversation.customerName || conversation.customerPhone}</strong>
            </p>
            {!currentContact && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                Este contato será criado automaticamente ao atribuir etiquetas.
              </p>
            )}
          </div>

          {/* Success Message */}
          {tagModalSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-green-800 dark:text-green-200">{tagModalSuccess}</span>
            </div>
          )}

          {/* Error Message */}
          {tagModalError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-2">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-red-800 dark:text-red-200">{tagModalError}</span>
            </div>
          )}

          <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
            {tags.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">
                Nenhuma etiqueta cadastrada. Crie uma etiqueta primeiro na tela de Contatos.
              </p>
            ) : (
              tags.map((tag) => (
                <label
                  key={tag.id}
                  className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTagIds([...selectedTagIds, tag.id]);
                      } else {
                        setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                      }
                    }}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <span
                    className="ml-3 px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                </label>
              ))
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowAssignTagModal(false);
                setTagModalError(null);
                setTagModalSuccess(null);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                if (tags.length === 0) {
                  setTagModalError('Nenhuma etiqueta cadastrada. Crie uma etiqueta primeiro na tela de Contatos.');
                  return;
                }

                setIsSavingTags(true);
                setTagModalError(null);
                setTagModalSuccess(null);

                try {
                  let contactId = currentContact?.id;

                  // If we don't have a contact yet, try to find it
                  if (!contactId) {
                    console.log('No current contact, searching...');
                    const found = await loadContact(conversation.customerPhone);

                    if (found) {
                      contactId = found.id;
                      console.log('Contact found:', contactId);
                    }
                  }

                  // If still no contact, create it
                  if (!contactId) {
                    console.log('Creating new contact for phone:', conversation.customerPhone);

                    try {
                      const newContact = await api.createContact({
                        name: conversation.customerName || 'Contato sem nome',
                        phone: conversation.customerPhone,
                      });
                      contactId = newContact.id;
                      setCurrentContact(newContact);
                      console.log('New contact created:', contactId);
                    } catch (createError: any) {
                      console.error('Failed to create contact:', createError);

                      // If it failed because contact exists, force reload and try once more
                      if (createError.response?.data?.error?.includes('já existe')) {
                        console.log('Contact exists but was not found. Force reloading...');

                        // Wait a bit and reload
                        await new Promise(resolve => setTimeout(resolve, 500));
                        const found = await loadContact(conversation.customerPhone);

                        if (found) {
                          contactId = found.id;
                          console.log('Contact found on force reload:', contactId);
                        } else {
                          throw new Error('Contato já existe no sistema mas não foi possível encontrá-lo. Tente novamente.');
                        }
                      } else {
                        throw createError;
                      }
                    }
                  }

                  // Now assign tags
                  if (contactId) {
                    console.log('Assigning tags to contact:', contactId, 'Tags:', selectedTagIds);
                    await api.assignTagsToContact(contactId, selectedTagIds);
                    setTagModalSuccess('Etiquetas atribuídas com sucesso!');

                    // Reload contact to show updated tags
                    await loadContact(conversation.customerPhone);

                    // Close modal after 1.5 seconds
                    setTimeout(() => {
                      setShowAssignTagModal(false);
                      setTagModalSuccess(null);
                    }, 1500);
                  } else {
                    setTagModalError('Erro ao criar ou encontrar o contato');
                  }
                } catch (error: any) {
                  console.error('Error assigning tags:', error);
                  setTagModalError(error.response?.data?.error || 'Erro ao atribuir etiquetas');
                } finally {
                  setIsSavingTags(false);
                }
              }}
              disabled={isSavingTags || tags.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSavingTags ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <span>Salvar</span>
              )}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Conversation History Modal */}
    <ConversationHistoryModal
      isOpen={showHistoryModal}
      onClose={() => setShowHistoryModal(false)}
      customerPhone={conversation?.customerPhone || ''}
      customerName={conversation?.customerName || undefined}
    />

    {/* Add Contact Confirmation Modal */}
    {showAddContactModal && conversation && (
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Adicionar Contato
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-2">
            Deseja adicionar este número à sua lista de contatos?
          </p>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">{conversation.customerPhone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <UserPlus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  Nome: <strong>{conversation.customerName || 'Cliente'}</strong>
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleAddContact}
              disabled={isAddingContact}
              className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isAddingContact && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              <span>{isAddingContact ? 'Adicionando...' : 'Sim, Adicionar'}</span>
            </button>
            <button
              onClick={() => setShowAddContactModal(false)}
              disabled={isAddingContact}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Add Contact Result Modal */}
    {showAddContactResultModal && addContactResult && (
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-center mb-4">
            <div className={`w-12 h-12 ${addContactResult.success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'} rounded-full flex items-center justify-center`}>
              {addContactResult.success ? (
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              )}
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
            {addContactResult.success ? 'Sucesso' : 'Aviso'}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
            {addContactResult.message}
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                setShowAddContactResultModal(false);
                setAddContactResult(null);
              }}
              className={`px-6 py-2 ${addContactResult.success ? 'bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600' : 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600'} text-white rounded-lg transition`}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
